import { useCommunitySlice } from 'app/components/Community/slice';
import { selectSliceCommunity } from 'app/components/Community/slice/selectors';
import { SORT_TYPE } from 'app/constants/enum';
import { UsernameFilter } from 'app/models';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useResultsPerPage } from '../resultsPerPage/useResultsPerPage';
import { safeReplaceState } from 'app/utils/safeHistory';

const COMMUNITY_SORTS = new Set<string>([
  SORT_TYPE.LATEST_UPLOADED_DESC,
  SORT_TYPE.TOP_MOVERS_7,
  SORT_TYPE.TOP_MOVERS_30,
  SORT_TYPE.TOP_MOVERS_90,
  SORT_TYPE.TOP_MOVERS_ALL,
  SORT_TYPE.USERNAME_ASC,
  SORT_TYPE.USERNAME_DESC,
]);

export const useCommunity = () => {
  const { perPageLocalStorage } = useResultsPerPage();
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { actions } = useCommunitySlice();
  const timeoutRef = useRef<any>(null);
  const lastUrlUpdateRef = useRef<number>(0);
  const prevUrlRef = useRef<string>('');
  const isHomePage = pathname.includes('/home');
  const isTrackPage = pathname.includes('/tracks');

  const {
    users = [],
    isLoading,
    pagination,
    isLoadingFollow,
    isLoadingMore,
    currentPage,
    totalPage,
    contributors,
    topContributors,
    isLoadingTopContributor,
    topContributorsCache,
  } = useSelector(selectSliceCommunity);

  const DEFAULT_FILTERS = useMemo(() => {
    return {
      sort: isHomePage
        ? SORT_TYPE.TOP_MOVERS_30
        : SORT_TYPE.LATEST_UPLOADED_DESC,
      search: '',
      page: 1,
      pageSize:
        pathname.includes('/contributors') || pathname.includes('/charts')
          ? perPageLocalStorage
          : 10,
      labelId: 'all',
    };
  }, [isHomePage, pathname, perPageLocalStorage]);
  const [filter, setFilter] = useState<UsernameFilter>(DEFAULT_FILTERS);
  const [searchValue, setSearchValue] = useState<string>('');

  const updateUrlWithFilters = useCallback(
    (newFilter: any) => {
      const queryParams = new URLSearchParams();

      if (newFilter.search) queryParams.set('search', newFilter.search);
      if (newFilter.sort) queryParams.set('sort', newFilter.sort);
      if (newFilter.labelId) queryParams.set('labelId', newFilter.labelId);
      const queryString = queryParams.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

      if (prevUrlRef.current === newUrl) {
        return;
      }

      const now = Date.now();
      if (now - lastUrlUpdateRef.current < 200) {
        return;
      }

      lastUrlUpdateRef.current = now;
      prevUrlRef.current = newUrl;
      safeReplaceState({ url: newUrl });
    },
    [pathname],
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const urlFilters: any = {};

    const searchParam = queryParams.get('search');
    if (searchParam) {
      urlFilters.search = searchParam;
      setSearchValue(searchParam);
    }

    const sortParam = queryParams.get('sort');
    if (sortParam) {
      if (COMMUNITY_SORTS.has(sortParam)) {
        urlFilters.sort = sortParam;
      } else {
        const sanitizedSort = SORT_TYPE.LATEST_UPLOADED_DESC;
        urlFilters.sort = sanitizedSort;
        updateUrlWithFilters({
          ...DEFAULT_FILTERS,
          ...urlFilters,
          sort: sanitizedSort,
        });
      }
    }
    const labelsParam = queryParams.get('labelId');
    if (labelsParam) {
      urlFilters.labelId = labelsParam;
    }
    // Only update filter if we have URL params and it's the initial load
    if (Object.keys(urlFilters).length > 0) {
      setFilter(current => ({
        ...current,
        ...urlFilters,
        page: 1,
      }));
    }
  }, [
    search,
    setFilter,
    setSearchValue,
    updateUrlWithFilters,
    DEFAULT_FILTERS,
  ]);

  const onGetCommunity = useCallback(
    (payload?: any) => {
      dispatch(
        actions.getCommunityRequest({
          filter: {
            ...filter,
            search: payload?.search || filter?.search,
            sort: payload?.sort || filter?.sort,
            labelId: payload?.labelId || filter?.labelId,
            pageSize: payload?.pageSize || filter?.pageSize,
          },
        }),
      );
    },
    [actions, dispatch, filter],
  );

  const updateTopContributor = useCallback(
    (data?: any) => {
      dispatch(actions.updateTopContributor(data));
    },
    [actions, dispatch],
  );

  const onGetTopCommunity = useCallback(
    (payload?: any) => {
      dispatch(
        actions.getTopCommunityRequest({
          filter: {
            ...filter,
            search: isTrackPage ? '' : payload?.search || filter?.search,
            sort: payload?.sort || filter?.sort,
            labelId: payload?.labelId || filter?.labelId,
          },
        }),
      );
    },
    [actions, dispatch, filter, isTrackPage],
  );

  const onGetContributors = useCallback(() => {
    dispatch(actions.getContributorsRequest());
  }, [actions, dispatch]);

  const onToggleBlockUser = useCallback(
    user => {
      dispatch(actions.toggleBlockUserRequest({ _id: user._id }));
    },
    [dispatch, actions],
  );

  const handleChange = useCallback(
    e => {
      setSearchValue(e.target.value);

      if (timeoutRef) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setFilter(current => ({
          ...current,
          search: e.target.value,
          page: 1,
        }));
        const newFilters = {
          ...filter,
          page: 1,
          search: e.target.value,
        };

        updateUrlWithFilters(newFilters);
      }, 350);
    },
    [filter, updateUrlWithFilters],
  );

  const handleChangeFilter = useCallback(
    (event, key) => {
      setFilter(current => ({
        ...current,
        [key]: event,
        page: 1,
      }));
      const newFilters = {
        ...filter,
        page: 1,
        [key]: event,
      };

      updateUrlWithFilters(newFilters);
    },
    [filter, updateUrlWithFilters],
  );

  const fetchMoreData = useCallback(() => {
    setFilter(current => {
      return {
        ...current,
        page: currentPage + 1,
      };
    });
  }, [currentPage]);

  const handleObserverLoadMoreUsers = useCallback(
    entities => {
      const target = entities[0];
      if (
        target.isIntersecting &&
        totalPage > currentPage &&
        currentPage !== 0
      ) {
        fetchMoreData();
      }
    },
    [currentPage, fetchMoreData, totalPage],
  );

  return {
    updateTopContributor,
    onGetCommunity,
    handleChange,
    handleChangeFilter,
    onToggleBlockUser,
    users,
    isLoading,
    pagination,
    isLoadingFollow,
    setFilter,
    handleObserverLoadMoreUsers,
    setSearchValue,
    isLoadingMore,
    currentPage,
    onGetContributors,
    contributors,
    searchValue,
    filter,
    totalPage,
    onGetTopCommunity,
    topContributors,
    isLoadingTopContributor,
    topContributorsCache,
  };
};
